/**
 * The URL surface of the shell.
 *
 * Paths live here rather than inline in App.tsx so a link, a route definition
 * and a test cannot drift apart. When they do drift the symptom is a link that
 * renders the not-found screen, which reads as a routing bug and is a typo.
 *
 * The patterns are duplicated as literals in App.tsx because preact-iso's
 * `<Route path>` needs a string it can match against; `ROUTE_PATTERNS` is what
 * the tests assert those literals against.
 */

export const APP_NAME = 'Puzzle Gate';

export const ROUTE_PATTERNS = {
  picker: '/',
  levelSelect: '/game/:gameId',
  play: '/play/:gameId/:levelId',
  settings: '/settings',
} as const;

/** Builders. Segments are encoded here so no caller has to remember to. */
export const routes = {
  picker: (): string => '/',
  levelSelect: (gameId: string): string => `/game/${encodeURIComponent(gameId)}`,
  play: (gameId: string, levelId: string): string =>
    `/play/${encodeURIComponent(gameId)}/${encodeURIComponent(levelId)}`,
  settings: (): string => '/settings',
};

/*
 * `KNOWN_GAME_IDS` and `isKnownGame` used to live here as a constant array, and
 * moved to `platform/game-registry.ts` in Sprint 1b. The array was enough while
 * the shell only had to know that an id was a valid URL segment; it stopped
 * being enough when the shell had to turn that id into an `import()`. Keeping
 * two lists -- one of ids the router accepts, one of modules that exist -- is a
 * `/game/match3` that routes to a screen with nothing behind it.
 *
 * SHELL-013 is still what they are checked against: without them,
 * `/game/anything` renders a level select for a game that does not exist and
 * fails later, somewhere further from the cause.
 */

/**
 * The document title for a screen (SHELL-018).
 *
 * Derived from the screen's own title rather than from the URL. A path-to-title
 * map would be a second copy of the routing table, and the two would disagree
 * first on the cases that matter: `/game/nosuchgame` displays the not-found
 * screen, so its title must say so.
 */
export function documentTitle(screenTitle: string): string {
  return screenTitle === APP_NAME ? APP_NAME : `${screenTitle} \u00b7 ${APP_NAME}`;
}
